import type { MetricCard, MetricCardsBlock } from "../contract/types";
import { Icon, type IconName } from "../components/Icon";
import type { BlockViewProps } from "./types";

/**
 * Tiles de indicadores.
 *
 * El contrato del tile: etiqueta en minúscula de frase, valor en semibold, y
 * un delta opcional con signo. El delta nunca se distingue solo por color:
 * lleva flecha y signo, así que se lee también sin distinguir el verde del rojo.
 */

const DELTA_ICON: Record<"up" | "down" | "flat", IconName> = {
  up: "arrow-up",
  down: "arrow-down",
  flat: "minus",
};

/**
 * ¿La unidad va delante o detrás del número?
 *
 * `S/ 14 826` se lee bien; `% 21,2` no. Como el contrato da valor y unidad por
 * separado, la posición se decide por la forma de la unidad: los símbolos de
 * moneda son prefijo y todo lo demás (%, x, años, semanas, pp) es sufijo.
 */
const PREFIX_UNIT = /^(s\/\.?|us\$|\$|€|£|usd|pen|eur)/i;

function isPrefixUnit(unit: string): boolean {
  return PREFIX_UNIT.test(unit.trim());
}

function MetricTile({ card }: { card: MetricCard }) {
  const direction = card.delta_direction ?? null;
  const unit = card.unit ?? null;
  const unitBefore = unit !== null && isPrefixUnit(unit);
  const unitAfter = unit !== null && !unitBefore;

  return (
    <div className="metric">
      <span className="metric__label">{card.label}</span>
      <span className="metric__value-row">
        {unitBefore ? <span className="metric__unit">{unit}</span> : null}
        <span className="metric__value">{card.value}</span>
        {unitAfter ? <span className="metric__unit">{unit}</span> : null}
      </span>
      {card.delta ? (
        <span className={`metric__delta metric__delta--${direction ?? "flat"}`}>
          {direction ? <Icon name={DELTA_ICON[direction]} size="0.75rem" /> : null}
          <span className="u-nums">{card.delta}</span>
        </span>
      ) : null}
      {card.hint ? <span className="metric__hint">{card.hint}</span> : null}
    </div>
  );
}

export function MetricCards({ block }: BlockViewProps<MetricCardsBlock>) {
  const cards = block.cards ?? [];
  if (cards.length === 0) {
    return <p className="atom__empty">Sin indicadores para mostrar.</p>;
  }

  return (
    <div
      className="atom atom--metric-cards metrics"
      data-count={cards.length > 4 ? "many" : "few"}
    >
      {cards.map((card, index) => (
        <MetricTile key={`${card.label}-${index}`} card={card} />
      ))}
    </div>
  );
}

export default MetricCards;
